(function bunstersGwpModule(global) {
  'use strict';

  var MANAGED_PROPERTY = '_bunsters_gwp_managed';
  var RULE_PROPERTY = '_bunsters_gwp_rule';
  var OPT_OUT_ATTRIBUTE = '_bunsters_gwp_optouts__';
  var OPT_OUT_ATTRIBUTE_PREFIX = '_bunsters_gwp_optout_';
  var SAUCE_CHOICE_ATTRIBUTE = '_bunsters_gwp_sauce_choice';

  function asId(value) {
    return String(value == null ? '' : value);
  }

  function getQualifyingQuantity(cart, qualifierVariantIds, qualifierVariantQuantities) {
    var qualifiers = new Set((qualifierVariantIds || []).map(asId));
    var bundleQuantities = qualifierVariantQuantities || {};
    return (cart && Array.isArray(cart.items) ? cart.items : []).reduce(function sum(total, line) {
      var variantId = asId(line.variant_id || line.id);
      var lineQuantity = Math.max(0, Number(line.quantity || 0));
      if (qualifiers.has(variantId)) return total + lineQuantity;
      var containedQuantity = Math.max(0, Number(bundleQuantities[variantId] || 0));
      return total + lineQuantity * containedQuantity;
    }, 0);
  }

  function getTierGiftIds(quantity, tiers) {
    var count = Number(quantity || 0);
    var tier = (tiers || []).slice().sort(function sort(a, b) {
      return Number(b.minimumQuantity) - Number(a.minimumQuantity);
    }).find(function matches(candidate) {
      return count >= Number(candidate.minimumQuantity);
    });
    return tier ? tier.giftVariantIds.map(asId) : [];
  }

  function getSelectedSauceGiftId(cart, config) {
    var choices = config && config.sauceChoices ? config.sauceChoices : null;
    if (!choices) return '';
    var defaultId = asId(choices.defaultVariantId);
    var allowed = new Set((choices.variantIds || []).map(asId));
    var attributeName = choices.attributeName || SAUCE_CHOICE_ATTRIBUTE;
    var attributes = cart && cart.attributes ? cart.attributes : {};
    var selected = asId(attributes[attributeName]);
    return allowed.has(selected) ? selected : defaultId;
  }

  function applySauceChoice(giftIds, cart, config) {
    var choices = config && config.sauceChoices ? config.sauceChoices : null;
    var ids = (giftIds || []).map(asId);
    if (!choices) return ids;
    var defaultId = asId(choices.defaultVariantId);
    var selectedId = getSelectedSauceGiftId(cart, config);
    return ids.map(function replaceDefaultSauce(giftId) {
      return giftId === defaultId && selectedId ? selectedId : giftId;
    });
  }

  function isManagedLine(line, ruleVersion) {
    var properties = line && line.properties ? line.properties : {};
    return properties[MANAGED_PROPERTY] === 'true' && properties[RULE_PROPERTY] === ruleVersion;
  }

  function parseOptOutState(raw) {
    if (!raw) return {version: 1, gifts: {}};
    try {
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || parsed.version !== 1 || !parsed.gifts || typeof parsed.gifts !== 'object') {
        return {version: 1, gifts: {}};
      }
      return {version: 1, gifts: Object.assign({}, parsed.gifts)};
    } catch (error) {
      return {version: 1, gifts: {}};
    }
  }

  function markGiftOptOut(state, giftVariantId) {
    var next = parseOptOutState(state);
    next.gifts[asId(giftVariantId)] = {suppressed: true, wasEligible: true};
    return next;
  }

  function restoreGift(state, giftVariantId) {
    var next = parseOptOutState(state);
    delete next.gifts[asId(giftVariantId)];
    return next;
  }

  function applyOptOutCommand(state, action, giftVariantId) {
    return action === 'restore'
      ? restoreGift(state, giftVariantId)
      : markGiftOptOut(state, giftVariantId);
  }

  function createSerialQueue() {
    var tail = Promise.resolve();
    return function enqueue(task) {
      var current = tail.catch(function ignorePriorFailure() {}).then(task);
      tail = current.catch(function keepQueueUsable() {});
      return current;
    };
  }

  function transitionOptOutState(state, eligibleGiftIds) {
    var next = parseOptOutState(state);
    var eligible = new Set((eligibleGiftIds || []).map(asId));

    Object.keys(next.gifts).forEach(function transition(giftId) {
      var entry = next.gifts[giftId];
      if (!entry || entry.suppressed !== true) {
        delete next.gifts[giftId];
        return;
      }

      if (!eligible.has(giftId) && entry.wasEligible !== false) {
        next.gifts[giftId] = {suppressed: true, wasEligible: false};
      } else if (eligible.has(giftId) && entry.wasEligible === false) {
        delete next.gifts[giftId];
      }
    });

    return next;
  }

  function getDesiredGiftIds(cart, config, optOutState) {
    var quantity = getQualifyingQuantity(
      cart,
      config.qualifierVariantIds,
      config.qualifierVariantQuantities
    );
    var eligible = applySauceChoice(getTierGiftIds(quantity, config.tiers), cart, config);
    var nextState = transitionOptOutState(optOutState, eligible);
    var desired = eligible.filter(function notSuppressed(giftId) {
      return !nextState.gifts[giftId] || nextState.gifts[giftId].suppressed !== true;
    });
    return {quantity: quantity, eligible: eligible, desired: desired, optOutState: nextState};
  }

  function getReconciliationPlan(cart, config, optOutState) {
    var desiredState = getDesiredGiftIds(cart, config, optOutState);
    var desired = new Set(desiredState.desired);
    var seen = new Set();
    var updates = {};
    var unavailable = [];

    (cart.items || []).forEach(function inspect(line) {
      if (!isManagedLine(line, config.ruleVersion)) return;
      var variantId = asId(line.variant_id || line.id);
      var duplicate = seen.has(variantId);
      var shouldExist = desired.has(variantId) && !duplicate;
      if (!shouldExist) {
        updates[line.key] = 0;
        return;
      }
      seen.add(variantId);
      if (Number(line.quantity) !== 1) updates[line.key] = 1;
    });

    var additions = desiredState.desired.filter(function missing(giftId) {
      var gift = config.gifts[giftId];
      if (!gift || gift.available === false) {
        unavailable.push(giftId);
        return false;
      }
      return !seen.has(giftId);
    });

    return {
      qualifyingQuantity: desiredState.quantity,
      eligibleGiftIds: desiredState.eligible,
      desiredGiftIds: desiredState.desired,
      optOutState: desiredState.optOutState,
      updates: updates,
      additions: additions,
      unavailable: unavailable,
    };
  }

  function getShippingProgress(subtotalCents, thresholdCents) {
    var subtotal = Math.max(0, Number(subtotalCents || 0));
    var threshold = Math.max(1, Number(thresholdCents || 9900));
    return {
      unlocked: subtotal >= threshold,
      remainingCents: Math.max(0, threshold - subtotal),
      percent: Math.min(100, Math.max(0, subtotal / threshold * 100)),
    };
  }

  function isRewardStepReached(quantity, minimum, maximum) {
    var count = Number(quantity || 0);
    var min = Number(minimum || 0);
    var max = maximum === null || maximum === undefined || maximum === '' ? null : Number(maximum);

    return count >= min && (max === null || count <= max);
  }

  function stableJson(value) {
    var gifts = value && value.gifts ? value.gifts : {};
    var ordered = {};
    Object.keys(gifts).sort().forEach(function order(key) {
      ordered[key] = gifts[key];
    });
    return JSON.stringify({version: 1, gifts: ordered});
  }

  function optOutAttributeName(giftVariantId) {
    return OPT_OUT_ATTRIBUTE_PREFIX + asId(giftVariantId) + '__';
  }

  function readCartOptOutState(attributes) {
    var cartAttributes = attributes || {};
    var state = parseOptOutState(cartAttributes[OPT_OUT_ATTRIBUTE]);
    Object.keys(cartAttributes).forEach(function read(key) {
      if (!key.startsWith(OPT_OUT_ATTRIBUTE_PREFIX) || !key.endsWith('__')) return;
      var giftId = key.slice(OPT_OUT_ATTRIBUTE_PREFIX.length, -2);
      var rawEntry = cartAttributes[key];
      if (!rawEntry) {
        delete state.gifts[giftId];
        return;
      }
      try {
        var entry = typeof rawEntry === 'string' ? JSON.parse(rawEntry) : rawEntry;
        if (entry && entry.suppressed === true) {
          state.gifts[giftId] = {
            suppressed: true,
            wasEligible: entry.wasEligible !== false,
          };
        }
      } catch (error) {
        delete state.gifts[giftId];
      }
    });
    return state;
  }

  function getOptOutAttributeUpdates(beforeState, afterState) {
    var before = parseOptOutState(beforeState);
    var after = parseOptOutState(afterState);
    var ids = new Set(Object.keys(before.gifts).concat(Object.keys(after.gifts)));
    var updates = {};
    ids.forEach(function diff(giftId) {
      var beforeEntry = before.gifts[giftId];
      var afterEntry = after.gifts[giftId];
      if (JSON.stringify(beforeEntry || null) === JSON.stringify(afterEntry || null)) return;
      updates[optOutAttributeName(giftId)] = afterEntry ? JSON.stringify(afterEntry) : '';
    });
    return updates;
  }

  function isNativeProductAdd(url, options) {
    var requestUrl = typeof url === 'string' ? url : (url && url.url) || '';
    return requestUrl.indexOf('/cart/add') !== -1
      && global.FormData
      && options
      && options.body instanceof global.FormData;
  }

  function recoverNativeProductAdd(error, suppliedDocument, suppliedProductForm) {
    var targetDocument = suppliedDocument || global.document;
    var productForms = suppliedProductForm
      ? [suppliedProductForm]
      : Array.from(targetDocument.querySelectorAll('product-form')).filter(function loading(productForm) {
        return Boolean(productForm.querySelector('[type="submit"].is-loading[disabled]'));
      }).slice(-1);
    productForms.forEach(function recover(productForm) {
      var button = productForm.querySelector('[type="submit"].is-loading[disabled]');
      if (!button) return;

      if (typeof productForm.addToCartError === 'function') {
        productForm.addToCartError({
          message: 'We could not add that item',
          description: 'Please check your connection and try again.',
        });
      } else {
        var holder = productForm.querySelector('[data-cart-errors-container]');
        if (holder) {
          holder.textContent = 'We could not add that item. Please check your connection and try again.';
          holder.classList.add('is-visible');
        }
      }

      button.classList.remove('is-loading', 'is-success');
      button.removeAttribute('disabled');
    });
    targetDocument.dispatchEvent(new global.CustomEvent('theme:cart-bar:reset-atc-animation', {bubbles: true}));
    if (global.console && typeof global.console.error === 'function') {
      global.console.error('Add to cart failed.', error);
    }
  }

  var core = {
    MANAGED_PROPERTY: MANAGED_PROPERTY,
    RULE_PROPERTY: RULE_PROPERTY,
    OPT_OUT_ATTRIBUTE: OPT_OUT_ATTRIBUTE,
    OPT_OUT_ATTRIBUTE_PREFIX: OPT_OUT_ATTRIBUTE_PREFIX,
    SAUCE_CHOICE_ATTRIBUTE: SAUCE_CHOICE_ATTRIBUTE,
    getQualifyingQuantity: getQualifyingQuantity,
    getTierGiftIds: getTierGiftIds,
    getSelectedSauceGiftId: getSelectedSauceGiftId,
    applySauceChoice: applySauceChoice,
    isManagedLine: isManagedLine,
    parseOptOutState: parseOptOutState,
    markGiftOptOut: markGiftOptOut,
    restoreGift: restoreGift,
    applyOptOutCommand: applyOptOutCommand,
    createSerialQueue: createSerialQueue,
    transitionOptOutState: transitionOptOutState,
    getDesiredGiftIds: getDesiredGiftIds,
    getReconciliationPlan: getReconciliationPlan,
    getShippingProgress: getShippingProgress,
    isRewardStepReached: isRewardStepReached,
    stableJson: stableJson,
    optOutAttributeName: optOutAttributeName,
    readCartOptOutState: readCartOptOutState,
    getOptOutAttributeUpdates: getOptOutAttributeUpdates,
    isNativeProductAdd: isNativeProductAdd,
    recoverNativeProductAdd: recoverNativeProductAdd,
  };

  global.BunstersGwpCore = core;
  if (typeof module !== 'undefined' && module.exports) module.exports = core;

  if (!global.document || !global.fetch) return;

  function getConfig() {
    var element = document.getElementById('bunsters-gwp-config');
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Bunsters gift configuration is invalid.', error);
      return null;
    }
  }

  function money(cents) {
    if (global.theme && typeof global.theme.formatMoney === 'function') {
      return global.theme.formatMoney(cents, global.theme.moneyFormat);
    }
    return new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD'}).format(cents / 100);
  }

  function fetchJson(url, options) {
    return global.fetch(url, options).then(function parse(response) {
      return response.json().catch(function empty() { return {}; }).then(function withPayload(payload) {
        if (!response.ok) {
          var message = payload.description || payload.message || ('Request failed (' + response.status + ')');
          throw new Error(message);
        }
        return payload;
      });
    });
  }

  var activeNativeProductForms = new WeakSet();
  var nativeFetch = global.fetch.bind(global);
  global.fetch = function bunstersResilientFetch(url, options) {
    var request = nativeFetch(url, options);
    if (!isNativeProductAdd(url, options)) return request;
    var productForm = Array.from(document.querySelectorAll('product-form')).filter(function unclaimedLoading(form) {
      return !activeNativeProductForms.has(form)
        && Boolean(form.querySelector('[type="submit"].is-loading[disabled]'));
    }).slice(-1)[0] || null;
    if (productForm) activeNativeProductForms.add(productForm);
    return request.then(function nativeAddSettled(response) {
      if (productForm) activeNativeProductForms.delete(productForm);
      return response;
    }, function nativeAddRejected(error) {
      if (productForm) {
        activeNativeProductForms.delete(productForm);
        recoverNativeProductAdd(error, null, productForm);
      }
      throw error;
    });
  };

  function NativeGiftEngine(config) {
    var localGiftCommandQueue = createSerialQueue();
    this.config = config;
    this.root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
    this.inFlight = null;
    this.pendingOptions = null;
    this.enqueueGiftCommand = function enqueueGiftCommand(task) {
      return localGiftCommandQueue(function runWithCrossTabLock() {
        if (global.navigator && global.navigator.locks && typeof global.navigator.locks.request === 'function') {
          return global.navigator.locks.request('bunsters-gwp-cart-mutation', {mode: 'exclusive'}, task);
        }
        return task();
      });
    };
    this.lastCart = null;
    this.lastMessage = '';
    this.lastStatusIsError = false;
    this.boundCartMutation = this.onCartMutation.bind(this);
    this.boundVisibility = this.onVisibility.bind(this);
    this.boundClick = this.onClickCapture.bind(this);
  }

  NativeGiftEngine.prototype.start = function start() {
    document.addEventListener('theme:cart:added', this.boundCartMutation);
    document.addEventListener('theme:cart:loaded', this.boundCartMutation);
    document.addEventListener('bunsters:gwp:reconcile', this.boundCartMutation);
    document.addEventListener('click', this.boundClick, true);
    document.addEventListener('visibilitychange', this.boundVisibility);
    global.addEventListener('focus', this.boundVisibility);
    this.reconcile({refresh: false, openCart: false, reason: 'initial-load'});
  };

  NativeGiftEngine.prototype.onCartMutation = function onCartMutation(event) {
    var requested = event && event.detail ? event.detail : {};
    this.reconcile({
      refresh: requested.refresh === true,
      openCart: requested.openCart === true,
      reason: event ? event.type : 'cart-mutation',
    });
  };

  NativeGiftEngine.prototype.onVisibility = function onVisibility() {
    if (!document.hidden) this.reconcile({refresh: false, openCart: false, reason: 'focus'});
  };

  NativeGiftEngine.prototype.onClickCapture = function onClickCapture(event) {
    var sauceToggle = event.target.closest('[data-bunsters-sauce-toggle]');
    if (sauceToggle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var originalText = sauceToggle.textContent;
      sauceToggle.disabled = true;
      sauceToggle.textContent = 'SWAPPING…';
      this.swapSauce(
        sauceToggle.getAttribute('data-target-variant-id'),
        sauceToggle.getAttribute('data-target-title')
      ).finally(function enable() {
        sauceToggle.disabled = false;
        sauceToggle.textContent = originalText;
      });
      return;
    }

    var remove = event.target.closest('[data-bunsters-managed-remove]');
    if (remove) {
      event.preventDefault();
      event.stopImmediatePropagation();
      remove.setAttribute('aria-disabled', 'true');
      this.optOut(remove.getAttribute('data-id'), remove.getAttribute('data-variant-id')).finally(function enable() {
        remove.removeAttribute('aria-disabled');
      });
      return;
    }

    var restore = event.target.closest('[data-bunsters-gift-restore]');
    if (restore) {
      event.preventDefault();
      restore.disabled = true;
      this.restore(restore.getAttribute('data-bunsters-gift-restore')).finally(function enable() {
        restore.disabled = false;
      });
      return;
    }

    var share = event.target.closest('[data-bunsters-cart-share]');
    if (share) {
      event.preventDefault();
      this.shareCart(share);
    }
  };

  NativeGiftEngine.prototype.getCart = function getCart() {
    return fetchJson(this.root + 'cart.js', {headers: {Accept: 'application/json'}});
  };

  NativeGiftEngine.prototype.updateCart = function updateCart(body) {
    return fetchJson(this.root + 'cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
  };

  NativeGiftEngine.prototype.addGifts = function addGifts(giftIds) {
    if (!giftIds.length) return Promise.resolve(null);
    var ruleVersion = this.config.ruleVersion;
    return fetchJson(this.root + 'cart/add.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({
        items: giftIds.map(function item(id) {
          return {
            id: Number(id),
            quantity: 1,
            properties: {
              _bunsters_gwp_managed: 'true',
              _bunsters_gwp_rule: ruleVersion,
            },
          };
        }),
      }),
    });
  };

  NativeGiftEngine.prototype.refreshGiftAvailability = function refreshGiftAvailability(giftIds) {
    var engine = this;
    return Promise.all((giftIds || []).map(function refresh(giftId) {
      var gift = engine.config.gifts[giftId];
      if (!gift || !gift.handle) return null;
      return fetchJson(engine.root + 'products/' + encodeURIComponent(gift.handle) + '.js', {
        headers: {Accept: 'application/json'},
      }).then(function update(product) {
        var variant = (product.variants || []).find(function matching(candidate) {
          return asId(candidate.id) === asId(giftId);
        });
        if (variant) gift.available = variant.available === true;
        return variant || null;
      }).catch(function preserveLastKnownAvailability() {
        return null;
      });
    }));
  };

  NativeGiftEngine.prototype.refreshDrawer = function refreshDrawer(openCart) {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
      bubbles: true,
      detail: {openCart: openCart === true},
    }));
  };

  NativeGiftEngine.prototype.setStatus = function setStatus(message, isError) {
    this.lastMessage = message || '';
    this.lastStatusIsError = isError === true;
    document.querySelectorAll('[data-bunsters-gwp-status]').forEach(function update(element) {
      element.textContent = message || '';
      element.classList.toggle('is-error', isError === true);
      element.hidden = !message;
    });
  };

  NativeGiftEngine.prototype.renderProgress = function renderProgress(cart, plan) {
    var config = this.config;
    var qualifierQuantity = plan ? plan.qualifyingQuantity : getQualifyingQuantity(
      cart,
      config.qualifierVariantIds,
      config.qualifierVariantQuantities
    );
    var managedIds = (cart.items || []).filter(function managed(line) {
      return isManagedLine(line, config.ruleVersion);
    }).map(function id(line) {
      return asId(line.variant_id || line.id);
    });
    var paidSubtotal = (cart.items || []).reduce(function sum(total, line) {
      return isManagedLine(line, config.ruleVersion) ? total : total + Number(line.final_line_price || 0);
    }, 0);
    var shipping = getShippingProgress(paidSubtotal, config.shippingThresholdCents);

    document.querySelectorAll('[data-bunsters-progress]').forEach(function render(holder) {
      var shippingMessage = holder.querySelector('[data-bunsters-shipping-message]');
      var shippingBar = holder.querySelector('[data-bunsters-shipping-progress]');
      if (shippingMessage) {
        shippingMessage.textContent = shipping.unlocked
          ? 'Free shipping unlocked'
          : 'Spend ' + money(shipping.remainingCents) + ' more for free shipping';
      }
      if (shippingBar) {
        shippingBar.value = shipping.percent;
        shippingBar.style.setProperty('--progress-width', shipping.percent + '%');
      }

      holder.querySelectorAll('[data-bunsters-reward-step]').forEach(function step(element) {
        var minimum = Number(element.getAttribute('data-minimum-quantity'));
        var maximum = element.getAttribute('data-maximum-quantity');
        element.classList.toggle('is-reached', isRewardStepReached(qualifierQuantity, minimum, maximum));
      });

      var shippingStep = holder.querySelector('[data-bunsters-shipping-step]');
      if (shippingStep) shippingStep.classList.toggle('is-reached', shipping.unlocked);

      var count = holder.querySelector('[data-bunsters-qualifying-count]');
      if (count) count.textContent = String(qualifierQuantity);

      var restores = holder.querySelector('[data-bunsters-gift-restores]');
      if (restores) {
        restores.textContent = '';
        Object.keys(plan && plan.optOutState ? plan.optOutState.gifts : {}).forEach(function button(giftId) {
          var entry = plan.optOutState.gifts[giftId];
          if (!entry || entry.suppressed !== true || !plan.eligibleGiftIds.includes(giftId)) return;
          var restoreButton = document.createElement('button');
          restoreButton.type = 'button';
          restoreButton.className = 'bunsters-cart-progress__restore';
          restoreButton.setAttribute('data-bunsters-gift-restore', giftId);
          restoreButton.textContent = 'Restore free gift: ' + ((config.gifts[giftId] && config.gifts[giftId].title) || 'gift');
          restores.appendChild(restoreButton);
        });
        restores.hidden = restores.childElementCount === 0;
      }
    });
  };

  NativeGiftEngine.prototype.reconcile = function reconcile(options) {
    var engine = this;
    var settings = Object.assign({refresh: false, openCart: false, reason: 'unknown'}, options || {});
    if (engine.inFlight) {
      engine.pendingOptions = {
        refresh: settings.refresh || Boolean(engine.pendingOptions && engine.pendingOptions.refresh),
        openCart: settings.openCart || Boolean(engine.pendingOptions && engine.pendingOptions.openCart),
        reason: settings.reason,
      };
      return engine.inFlight;
    }

    engine.inFlight = engine.runReconcile(settings).finally(function finish() {
      engine.inFlight = null;
      if (engine.pendingOptions) {
        var pendingOptions = engine.pendingOptions;
        engine.pendingOptions = null;
        engine.reconcile(pendingOptions);
      }
    });
    return engine.inFlight;
  };

  NativeGiftEngine.prototype.runReconcile = async function runReconcile(options) {
    try {
      var cart = await this.getCart();
      var originalState = readCartOptOutState(cart.attributes);
      var eligibleState = getDesiredGiftIds(cart, this.config, originalState);
      await this.refreshGiftAvailability(eligibleState.eligible);
      var plan = getReconciliationPlan(cart, this.config, originalState);
      var optOutAttributeUpdates = getOptOutAttributeUpdates(originalState, plan.optOutState);
      var updateKeys = Object.keys(plan.updates);
      var hasStateChange = Object.keys(optOutAttributeUpdates).length > 0;
      var mutated = false;

      if (updateKeys.length || hasStateChange) {
        var updateBody = {};
        if (updateKeys.length) updateBody.updates = plan.updates;
        if (hasStateChange) {
          updateBody.attributes = optOutAttributeUpdates;
        }
        cart = await this.updateCart(updateBody);
        mutated = true;
      }

      if (plan.additions.length) {
        await this.addGifts(plan.additions);
        mutated = true;
      }

      if (mutated) cart = await this.getCart();
      plan = getReconciliationPlan(cart, this.config, plan.optOutState);
      this.lastCart = cart;
      this.renderProgress(cart, plan);

      if (plan.unavailable.length) {
        var names = plan.unavailable.map(function name(id) {
          return (this.config.gifts[id] && this.config.gifts[id].title) || 'A free gift';
        }, this);
        this.setStatus(names.join(' and ') + (names.length > 1 ? ' are' : ' is') + ' unavailable right now. You can still check out.', true);
      } else if (mutated) {
        this.setStatus('Your free gifts are up to date.', false);
      } else if (this.lastMessage) {
        this.setStatus(this.lastMessage, this.lastStatusIsError);
      }

      if (mutated || options.refresh) this.refreshDrawer(options.openCart);
      return cart;
    } catch (error) {
      if (plan && plan.additions && plan.additions.length) {
        await this.refreshGiftAvailability(plan.additions);
      }
      var unavailableAfterFailure = plan && plan.additions
        ? plan.additions.filter(function unavailable(id) {
          return this.config.gifts[id] && this.config.gifts[id].available === false;
        }, this)
        : [];
      if (unavailableAfterFailure.length) {
        var unavailableNames = unavailableAfterFailure.map(function name(id) {
          return this.config.gifts[id].title || 'A free gift';
        }, this);
        this.setStatus(
          unavailableNames.join(' and ')
            + (unavailableNames.length > 1 ? ' are' : ' is')
            + ' unavailable right now. You can still check out.',
          true
        );
      } else {
        this.setStatus('We could not update your free gifts. Your cart is safe and you can still check out. ' + error.message, true);
      }
      if (options.refresh) this.refreshDrawer(options.openCart);
      return null;
    }
  };

  NativeGiftEngine.prototype.optOut = function optOut(lineKey, giftVariantId) {
    var engine = this;
    return engine.enqueueGiftCommand(async function runOptOut() {
      try {
        if (engine.inFlight) await engine.inFlight;
        var cart = await engine.getCart();
        var currentState = readCartOptOutState(cart.attributes);
        var state = applyOptOutCommand(
          currentState,
          'remove',
          giftVariantId
        );
        var currentLine = (cart.items || []).find(function matchingManagedLine(line) {
          return asId(line.variant_id || line.id) === asId(giftVariantId)
            && isManagedLine(line, engine.config.ruleVersion);
        });
        var attributes = getOptOutAttributeUpdates(currentState, state);
        if (cart.attributes && cart.attributes[OPT_OUT_ATTRIBUTE]) attributes[OPT_OUT_ATTRIBUTE] = '';
        var body = {attributes: attributes};
        if (currentLine) {
          body.updates = {};
          body.updates[currentLine.key] = 0;
        }
        await engine.updateCart(body);
        engine.setStatus('Free gift removed. Restore it any time while you still qualify.', false);
        await engine.reconcile({refresh: true, openCart: true, reason: 'gift-opt-out'});
      } catch (error) {
        engine.setStatus('We could not remove that free gift. Please try again.', true);
        engine.refreshDrawer(true);
      }
    });
  };

  NativeGiftEngine.prototype.restore = function restore(giftVariantId) {
    var engine = this;
    return engine.enqueueGiftCommand(async function runRestore() {
      try {
        if (engine.inFlight) await engine.inFlight;
        var cart = await engine.getCart();
        var currentState = readCartOptOutState(cart.attributes);
        var state = applyOptOutCommand(
          currentState,
          'restore',
          giftVariantId
        );
        var attributes = getOptOutAttributeUpdates(currentState, state);
        if (cart.attributes && cart.attributes[OPT_OUT_ATTRIBUTE]) attributes[OPT_OUT_ATTRIBUTE] = '';
        await engine.updateCart({attributes: attributes});
        engine.setStatus('Restoring your free gift.', false);
        await engine.reconcile({refresh: true, openCart: true, reason: 'gift-restore'});
      } catch (error) {
        engine.setStatus('We could not restore that free gift. Please try again.', true);
      }
    });
  };

  NativeGiftEngine.prototype.swapSauce = function swapSauce(targetVariantId, targetTitle) {
    var engine = this;
    return engine.enqueueGiftCommand(async function runSauceSwap() {
      try {
        if (engine.inFlight) await engine.inFlight;
        var choices = engine.config.sauceChoices || {};
        var targetId = asId(targetVariantId);
        var allowed = new Set((choices.variantIds || []).map(asId));
        if (!allowed.has(targetId)) throw new Error('That sauce choice is not available.');

        var cart = engine.lastCart || await engine.getCart();
        var quantity = getQualifyingQuantity(
          cart,
          engine.config.qualifierVariantIds,
          engine.config.qualifierVariantQuantities
        );
        var tierGifts = getTierGiftIds(quantity, engine.config.tiers);
        if (!tierGifts.includes(asId(choices.defaultVariantId))) {
          throw new Error('Your cart no longer qualifies for a free sauce.');
        }

        if (!engine.config.gifts[targetId] || engine.config.gifts[targetId].available === false) {
          throw new Error((targetTitle || 'That sauce') + ' is unavailable right now.');
        }

        var currentId = getSelectedSauceGiftId(cart, engine.config);
        if (currentId === targetId) return cart;

        var updates = {};
        (cart.items || []).forEach(function removeManagedSauce(line) {
          var variantId = asId(line.variant_id || line.id);
          if (allowed.has(variantId) && isManagedLine(line, engine.config.ruleVersion)) {
            updates[line.key] = 0;
          }
        });

        var attributeName = choices.attributeName || SAUCE_CHOICE_ATTRIBUTE;
        var body = {attributes: {}};
        body.attributes[attributeName] = targetId;
        if (Object.keys(updates).length) body.updates = updates;
        cart = await engine.updateCart(body);
        engine.setStatus('Switching your free sauce to ' + (targetTitle || 'your choice') + '.', false);
        await engine.addGifts([targetId]);
        cart = await engine.getCart();
        var optOutState = readCartOptOutState(cart.attributes);
        var plan = getReconciliationPlan(cart, engine.config, optOutState);
        engine.lastCart = cart;
        engine.renderProgress(cart, plan);
        engine.setStatus('Your free sauce is now ' + (targetTitle || 'updated') + '.', false);
        engine.refreshDrawer(true);
        return cart;
      } catch (error) {
        engine.setStatus(error.message || 'We could not swap your free sauce. Please try again.', true);
        engine.refreshDrawer(true);
        return null;
      }
    });
  };

  NativeGiftEngine.prototype.shareCart = async function shareCart(button) {
    var cart;
    try {
      cart = await this.getCart();
    } catch (error) {
      this.setStatus('We could not create a cart link. Please try again.', true);
      return;
    }

    var paidLines = cart.items.filter(function paid(line) {
      return !isManagedLine(line, this.config.ruleVersion);
    }, this);
    var path = paidLines.length
      ? '/cart/' + paidLines.map(function entry(line) { return line.variant_id + ':' + line.quantity; }).join(',')
      : '/cart';
    var url = global.location.origin + path;

    try {
      if (!global.navigator.clipboard || typeof global.navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }
      await global.navigator.clipboard.writeText(url);
      var original = button.textContent;
      button.textContent = 'Cart link copied';
      setTimeout(function reset() { button.textContent = original; }, 1800);
    } catch (error) {
      document.querySelectorAll('[data-bunsters-gwp-status]').forEach(function fallback(element) {
        element.textContent = '';
        var label = document.createElement('label');
        label.textContent = 'Copy this cart link:';
        var input = document.createElement('input');
        input.type = 'text';
        input.readOnly = true;
        input.value = url;
        input.setAttribute('aria-label', 'Cart link');
        label.appendChild(input);
        element.appendChild(label);
        element.classList.remove('is-error');
        element.hidden = false;
        input.focus();
        input.select();
      });
    }
  };

  function boot() {
    var config = getConfig();
    if (!config || global.BunstersGiftEngine) return;
    global.BunstersGiftEngine = new NativeGiftEngine(config);
    global.BunstersGiftEngine.start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {once: true});
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
